import { ChangeDetectionStrategy, Component, inject, AfterViewInit, viewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Dependency } from '@r1-compass-library/models/dependencies.model';
import { MatButtonModule } from '@angular/material/button';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatDialogModule } from '@angular/material/dialog';
import { Router } from '@angular/router';

export interface DisplayAssetDependencies {
    dependencies: Dependency[];
    dialogText: string;
    deleteAll?: boolean;
    dialogTitle?: string;
    cancelButtonText?: string;
}

@Component({
    selector: 'r1-compass-lib-display-asset-dependencies-list',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./display-asset-dependencies-list.component.scss'],
    imports: [MatButtonModule, MatDialogModule, MatTableModule, MatSortModule],
    template: `
        <h1 mat-dialog-title class="dialog-title">{{ data.dialogTitle ?? 'Dependencies' }}</h1>
        <div mat-dialog-content class="dialog-content">
            <p>{{ dialogText ?? "The asset you've chosen has the following dependencies:" }}</p>

            <table
                mat-table
                matSort
                matSortActive="playId"
                matSortDirection="desc"
                [dataSource]="dataDependencies"
                class="mat-elevation-z8 demo-table">
                @for (column of columns; track column.columnDef) {
                    <ng-container [matColumnDef]="column.columnDef">
                        <th
                            mat-header-cell
                            *matHeaderCellDef
                            [mat-sort-header]="column.columnDef"
                            [disabled]="!column.sortable">
                            {{ column.header }}
                        </th>

                        <td mat-cell *matCellDef="let row">
                            @if (column.columnDef === 'playId') {
                                {{ row.playId }}
                            } @else if (column.columnDef === 'name') {
                                {{ row.name }}
                            } @else {
                                <button
                                    mat-stroked-button
                                    onclick="blur()"
                                    (click)="routeToPlay(row.playId)"
                                    type="button">
                                    View Play
                                </button>
                            }
                        </td>
                    </ng-container>
                }

                <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
                <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
            </table>
        </div>
        <div mat-dialog-actions class="action-buttons">
            <button mat-raised-button (click)="dialogRef.close()" type="button" color="primary" id="cancel-button">
                {{ data.cancelButtonText ?? 'Cancel' }}
            </button>
            @if (data.deleteAll) {
                <button mat-stroked-button (click)="dialogRef.close({ deleteAll: true })" type="button">
                    Delete All
                </button>
            }
        </div>
    `,
})
export class DisplayAssetDependenciesListComponent implements AfterViewInit {
    readonly #router = inject(Router);
    protected dialogRef = inject<MatDialogRef<DisplayAssetDependenciesListComponent>>(MatDialogRef);
    protected data = inject<DisplayAssetDependencies>(MAT_DIALOG_DATA);

    protected readonly sort = viewChild.required(MatSort);

    protected dataDependencies = new MatTableDataSource(this.data.dependencies ?? []);
    protected dialogText: string | undefined = this.data.dialogText;
    protected columns = [
        { header: 'Play ID', columnDef: 'playId', sortable: true },
        { header: 'Name', columnDef: 'name', sortable: true },
        { header: 'Link', columnDef: 'link', sortable: false },
    ];
    protected displayedColumns = ['playId', 'name', 'link'];

    ngAfterViewInit() {
        this.dataDependencies.sort = this.sort();
    }

    protected routeToPlay(playId: number | undefined) {
        const url = this.#router.serializeUrl(this.#router.createUrlTree(['/plays', playId, 'definition']));
        window.open(url);
    }
}
